/**
 * TradeCard Component
 *
 * Condensed (default) and expanded trade card for the Trades pane list.
 *
 * Condensed layout:
 *   Row 1: trade name (truncated)  +  status badge  +  chevron toggle
 *   Row 2: context line — varies by status:
 *     Open:    entry model(s) · Criteria: X/Y · +N/-N  (criteria gated by cardInfo)
 *     Shelved: entry model(s) · Criteria: X/Y · Incomplete
 *     Missed:  "reason text" · reason category
 *   Row 3: playbook sub-label  (optional, gated by cardInfo.playbook)
 *
 * Expanded additions (chevron toggle):
 *   Open:    Entry Models list · Criteria/Confluence detail · Images count
 *   Shelved: Entry Models list · Criteria/Confluence detail · "Incomplete" hint
 *   Missed:  Opportunity detail (phase2Opportunity)
 *   All:     Last modified  (optional, gated by cardInfo.modified)
 *
 * Tapping the card body → opens edit drawer via onEdit.
 * Chevron button → toggles expanded view without opening the drawer.
 *
 * Notes count is not shown — notes live in NoteDraftService (async); deferred to T07.
 *
 * WU2A: OpportunityDraft (artifactKind: 'opportunity') renders as a minimal fallback —
 *       tradeName + Opportunity badge only, no phase1 detail sections.
 *       Full OpportunityCard replaces this in WU03 T06.
 *
 * @see TradesPane — renders the card list; provides cardInfo and onEdit
 * @see TradeDrawer — opened in edit mode when onEdit fires (for artifactKind: 'trade')
 */

// 1. External dependencies
import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// 2. Internal absolute imports
import { Button, Chip } from '@enfomentis/ui';

// 3. Type imports
import type { TradeDraft } from '@/types/transient/TradeDraft';
import type { LiveTradingSettings } from '@/types/schema/LiveTradingSettings';

// ===== TYPES =====

type CardStatus = 'open' | 'shelved' | 'ready' | 'opportunity';
type CardInfoEntry = LiveTradingSettings['activeTradesCardInfo'][number];

export interface TradeCardProps {
  trade: TradeDraft;
  /** Controls which optional fields appear on the card */
  cardInfo: LiveTradingSettings['activeTradesCardInfo'];
  /** Called when user taps the card body to open the edit drawer */
  onEdit: (tradeId: string) => void;
}

// ===== HELPERS =====

function deriveStatus(trade: TradeDraft): CardStatus {
  if (trade.artifactKind === 'opportunity') return 'opportunity';
  if (trade.isShelved) return 'shelved';
  if (trade.workflowStatus === 'ready-for-review') return 'ready';
  return 'open';
}

function isFieldShown(cardInfo: CardInfoEntry[], field: CardInfoEntry['field']): boolean {
  return cardInfo.find((e) => e.field === field)?.show ?? false;
}

// ===== COMPONENT =====

export const TradeCard: React.FC<TradeCardProps> = React.memo(({ trade, cardInfo, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const status = deriveStatus(trade);

  // ---------------------------------------------------------------------------
  // Derived values — phase1 fields are only available on TradeSetupDraft
  // ---------------------------------------------------------------------------

  const isTradeSetup = trade.artifactKind === 'trade';

  const checkedModels   = isTradeSetup ? (trade.phase1.entryModelSnapshot?.filter((em) => em.checked) ?? []) : [];
  const criteriaTotal   = isTradeSetup ? (trade.phase1.criteriaSnapshot?.length ?? 0) : 0;
  const criteriaMet     = isTradeSetup ? (trade.phase1.criteriaSnapshot?.filter((c) => c.checked).length ?? 0) : 0;
  const posPresent      = isTradeSetup ? (trade.phase1.confluenceSnapshot?.filter((c) => c.polarity === 'positive' && c.checked).length ?? 0) : 0;
  const negPresent      = isTradeSetup ? (trade.phase1.confluenceSnapshot?.filter((c) => c.polarity === 'negative' && c.checked).length ?? 0) : 0;
  const imageCount      = isTradeSetup
    ? Object.values(trade.phase1.images ?? {}).reduce((sum, arr) => sum + (arr?.length ?? 0), 0)
    : 0;

  const playbookName    = isTradeSetup ? trade.phase1.playbookSnapshot?.name : undefined;
  const reasonText      = !isTradeSetup ? trade.phase2Opportunity?.reasonSnapshot?.name : undefined;
  const reasonCategory  = !isTradeSetup ? trade.phase2Opportunity?.reasonCategorySnapshot?.name : undefined;

  const showCriteria    = isFieldShown(cardInfo, 'criteria');
  const showPlaybook    = isFieldShown(cardInfo, 'playbook');
  const showModified    = isFieldShown(cardInfo, 'modified');

  // ---------------------------------------------------------------------------
  // Condensed info line (plain string — enables truncation)
  // ---------------------------------------------------------------------------

  let infoLine: string;

  if (status === 'opportunity') {
    const parts = [
      reasonText ? `"${reasonText}"` : null,
      reasonCategory ?? null,
    ].filter((p): p is string => p !== null);
    infoLine = parts.length > 0 ? parts.join(' · ') : 'No reason recorded';
  } else {
    const parts = [
      checkedModels.length > 0 ? checkedModels.map((m) => m.name).join(', ') : null,
      showCriteria && criteriaTotal > 0 ? `Criteria: ${criteriaMet}/${criteriaTotal}` : null,
      showCriteria && criteriaTotal > 0 ? `+${posPresent}/-${negPresent}` : null,
      status === 'shelved' ? 'Incomplete' : null,
    ].filter((p): p is string => p !== null);
    infoLine = parts.join(' · ');
  }

  // ---------------------------------------------------------------------------
  // Modified time label (shared across expanded variants)
  // ---------------------------------------------------------------------------

  const modifiedLabel = showModified
    ? new Date(trade.updatedAt).toLocaleTimeString('en-US', {
        hour: '2-digit', minute: '2-digit', hour12: false,
      })
    : null;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div
      className="rounded-md border border-default-200 bg-default-50 px-2.5 py-2 cursor-pointer hover:border-default-300 hover:bg-default-100 transition-colors"
      onClick={() => { onEdit(trade.id); }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit(trade.id);
        }
      }}
    >
      {/* ── Row 1: trade name + status badge + chevron ── */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-text-primary truncate flex-1 leading-snug">
          {trade.tradeName ?? 'Unnamed trade'}
        </p>

        <div className="flex items-center gap-0.5 shrink-0">
          <Chip
            size="sm"
            variant="flat"
            color={
              status === 'open' ? 'success' :
              status === 'ready' ? 'primary' :
              status === 'shelved' ? 'warning' :
              'default' // opportunity
            }
          >
            {status === 'open' ? '● Open'
              : status === 'ready' ? '✓ Ready'
              : status === 'shelved' ? '◐ Shelved'
              : '○ Opportunity'}
          </Chip>

          {/* Chevron: stops propagation so card-click doesn't also fire */}
          <div onClick={(e) => { e.stopPropagation(); }}>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              className="h-5 w-5 min-w-0"
              onPress={() => { setIsExpanded((v) => !v); }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Row 2: condensed info line ── */}
      {infoLine.length > 0 && (
        <p
          className={`text-xs mt-0.5 truncate ${
            status === 'opportunity' && !reasonText ? 'text-text-tertiary italic' : 'text-text-secondary'
          }`}
        >
          {infoLine}
        </p>
      )}

      {/* ── Row 3: optional playbook sub-label ── */}
      {showPlaybook && playbookName && (
        <p className="text-xs text-text-tertiary italic mt-0.5 truncate">{playbookName}</p>
      )}

      {/* ── Expanded content ── */}
      {isExpanded && (
        <div className="flex flex-col gap-0.5 mt-1.5 pt-1.5 border-t border-default-100">

          {status === 'opportunity' && (
            <p className="text-xs text-text-tertiary italic">
              Full opportunity detail available in WU03.
            </p>
          )}

          {status === 'shelved' && (
            <>
              {checkedModels.length > 0 && (
                <p className="text-xs text-text-secondary">
                  Entry Models: {checkedModels.map((m) => m.name).join(', ')}
                </p>
              )}
              {criteriaTotal > 0 && (
                <p className="text-xs text-text-secondary">
                  Criteria: {criteriaMet}/{criteriaTotal} met · Confluence: +{posPresent} / -{negPresent}
                </p>
              )}
              <p className="text-xs text-warning font-medium">Incomplete — tap to continue</p>
            </>
          )}

          {status === 'open' && (
            <>
              {checkedModels.length > 0 && (
                <p className="text-xs text-text-secondary">
                  Entry Models: {checkedModels.map((m) => m.name).join(', ')}
                </p>
              )}
              {criteriaTotal > 0 && (
                <p className="text-xs text-text-secondary">
                  Criteria: {criteriaMet}/{criteriaTotal} met · Confluence: +{posPresent} positive / -{negPresent} negative
                </p>
              )}
              {imageCount > 0 && (
                <p className="text-xs text-text-secondary">Images: {imageCount}</p>
              )}
            </>
          )}

          {modifiedLabel && (
            <p className="text-xs text-text-tertiary">Modified {modifiedLabel}</p>
          )}
        </div>
      )}
    </div>
  );
});

TradeCard.displayName = 'TradeCard';
